import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentService } from '../services/document.service';
import {
  DocFile,
  DocumentDetail,
  DocumentQuery,
  DocumentSummary,
  LookupOption,
  PagedResult,
  SecurityAccess,
} from '../models/document.models';

/** A parsed OnBase Unity e-form: header fields + any repeater tables (e.g. GL distribution). */
interface ParsedForm {
  fields: { label: string; value: string }[];
  tables: { title: string; columns: string[]; rows: Record<string, string>[] }[];
}

@Component({
  selector: 'app-ap-viewer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ap-viewer.component.html',
  styleUrls: ['./ap-viewer.component.css'],
})
export class ApViewerComponent implements OnInit {
  // ---- Filter state ----
  filters: DocumentQuery = {
    company: '',
    category: '',
    classification: '',
    vendor: '',
    approver: '',
    glAcct: '',
    dateFrom: '',
    dateTo: '',
    amount: '',
    docNumber: '',
    checkNumber: '',
    poNumber: '',
    sortBy: 'docDate',
    sortDir: 'desc',
    page: 1,
    pageSize: 25,
  };

  companies: LookupOption[] = [];
  categories: LookupOption[] = [];
  classifications: LookupOption[] = [];

  // ---- Classification multi-select (#274982) ----
  // filters.classification carries the CSV of selected ids ('2,3,8') sent to the API.
  selectedClassificationIds: string[] = [];
  classificationMenuOpen = false;

  // ---- GL Acct typeahead combo (code + description, company-scoped) ----
  glAccounts: LookupOption[] = [];
  glFiltered: LookupOption[] = [];
  glInputText = '';
  showGlList = false;

  // ---- Vendor typeahead combo (VendorID + name, company-scoped) ----
  vendors: LookupOption[] = [];
  vendorsFiltered: LookupOption[] = [];
  vendorInputText = '';
  showVendorList = false;

  // ---- Approver typeahead combo (name only, company-scoped, AP-only) ----
  approvers: LookupOption[] = [];
  approversFiltered: LookupOption[] = [];
  approverInputText = '';
  showApproverList = false;

  // ---- Security (Harrell's model) ----
  access: SecurityAccess | null = null;
  accessChecked = false;   // security call finished
  accessDenied = false;    // no DocRepo app role (144) -> show access-denied message

  /** Categories that have a real data model; anything else renders a "coming soon" state. */
  private static readonly SupportedCategories = ['AP', 'AR'];

  /** Category the CURRENT results belong to (set at refresh) - drives grid/pane/detail behavior. */
  activeCategory = 'AP';

  // ---- Results ----
  result: PagedResult<DocumentSummary> | null = null;
  loading = false;
  error: string | null = null;
  hasSearched = false;

  // ---- Row expand / detail cache ----
  expandedId: number | null = null;
  details: Record<number, DocumentDetail> = {};
  detailLoading = false;

  // ---- PDF pane ----
  paneOpen = false;
  paneDoc: DocumentDetail | null = null;
  paneIndex = 0;
  paneFileName: string | null = null;
  paneFileKind: 'pdf' | 'image' | 'tiff' | 'text' | 'xmlform' | 'other' | 'none' = 'none';
  paneSafeUrl: SafeResourceUrl | null = null;
  paneRawUrl: string | null = null;
  /** Parsed OnBase Unity e-form (GL Coding Form etc.) for the 'xmlform' preview. */
  paneForm: ParsedForm | null = null;

  // ---- Email-a-document form (inline in the pane) ----
  emailFormOpen = false;
  emailTo = '';
  emailMessage = '';
  emailSending = false;
  emailError: string | null = null;
  emailSent = false;

  constructor(private svc: DocumentService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    // Gate the whole app on the user's Harrell's security roles first. The allowed Company/Category
    // options come from their Access roles; no app role (144) => access-denied.
    this.loadAccess();
  }

  private loadAccess(): void {
    this.svc.getAccess().subscribe({
      next: (a) => {
        this.access = a;
        this.accessChecked = true;
        if (!a.hasAppAccess) {
          this.accessDenied = true;
          return;
        }
        this.companies = a.companies.map((c) => ({ value: c.companyCode, label: c.companyName }));
      },
      error: () => {
        this.accessChecked = true;
        this.accessDenied = true;
      },
    });
  }

  onCompanyChange(): void {
    // Category and Classification are both company-specific; reset and reload for the new company.
    this.filters.category = '';
    this.clearClassificationSelection();
    this.categories = [];
    this.classifications = [];
    this.resetGlFilter();
    this.resetVendorFilter();
    this.resetApproverFilter();
    this.result = null;
    this.hasSearched = false;
    if (!this.filters.company) return;
    this.loadCategoriesFromAccess(); // sets filters.category (AP default)
    this.loadCategoryLookups();
  }

  /**
   * #274982 - Manual Check's special check-# mode only applies when it is the SOLE selection.
   * Selected alongside any other classification it is just another id in the IN filter (no check #
   * field, dates still required). So: exactly one selected AND it is Manual Check.
   */
  get isManualCheckClassification(): boolean {
    if (this.selectedClassificationIds.length !== 1) return false;
    const sel = this.classifications.find((c) => c.value === this.selectedClassificationIds[0]);
    return (sel?.label || '').trim().toLowerCase() === 'manual check';
  }

  /** Classification changed: drop the check-number filter unless it's still a lone Manual Check. */
  onClassificationChanged(): void {
    if (!this.isManualCheckClassification) this.filters.checkNumber = '';
  }

  /** #274982 multi-select helpers for the classification checkbox dropdown. */
  isClassificationSelected(value: string): boolean {
    return this.selectedClassificationIds.includes(value);
  }
  toggleClassification(value: string): void {
    const i = this.selectedClassificationIds.indexOf(value);
    if (i >= 0) this.selectedClassificationIds.splice(i, 1);
    else this.selectedClassificationIds.push(value);
    this.filters.classification = this.selectedClassificationIds.join(',');
    this.onClassificationChanged();
  }
  toggleClassificationMenu(): void {
    if (!this.filters.company || this.showComingSoon) return;
    this.classificationMenuOpen = !this.classificationMenuOpen;
  }
  private clearClassificationSelection(): void {
    this.selectedClassificationIds = [];
    this.classificationMenuOpen = false;
    this.filters.classification = '';
  }
  get classificationSummary(): string {
    const n = this.selectedClassificationIds.length;
    if (n === 0) return 'Select…';
    if (n === 1) {
      const sel = this.classifications.find((c) => c.value === this.selectedClassificationIds[0]);
      return sel?.label ?? '1 selected';
    }
    return `${n} selected`;
  }

  /** Category changed: reset classification/results and reload the category-specific lookups. */
  onCategoryChange(): void {
    this.clearClassificationSelection();
    this.classifications = [];
    this.resetVendorFilter();
    this.resetGlFilter();
    this.resetApproverFilter();
    this.result = null;
    this.hasSearched = false;
    this.closePane();
    this.loadCategoryLookups();
  }

  /** Load the lookups for the selected company + category: classifications + party always; GL +
   *  approver only for AP (AR has neither). Skips unsupported ("coming soon") categories. */
  private loadCategoryLookups(): void {
    // Wait for a company AND a chosen category (classifications/party are category-specific).
    if (!this.filters.company || !this.filters.category || this.showComingSoon) return;
    this.loadClassifications();
    this.loadVendors();
    if (this.filters.category === 'AP') {
      this.loadGlAccounts();
      this.loadApprovers();
    }
  }

  /** Reset every filter back to the initial "pick a company" state. */
  clearFilters(): void {
    this.selectedClassificationIds = [];
    this.classificationMenuOpen = false;
    this.filters = {
      company: '',
      category: '',
      classification: '',
      vendor: '',
      approver: '',
      glAcct: '',
      dateFrom: '',
      dateTo: '',
      amount: '',
      docNumber: '',
      poNumber: '',
      sortBy: 'docDate',
      sortDir: 'desc',
      page: 1,
      pageSize: 25,
    };
    this.categories = [];
    this.classifications = [];
    this.resetGlFilter();
    this.resetVendorFilter();
    this.resetApproverFilter();
    this.result = null;
    this.hasSearched = false;
    this.closePane();
  }

  loadApprovers(): void {
    this.svc.getApprovers(this.filters.company).subscribe({
      next: (a) => {
        this.approvers = a;
        this.approversFiltered = a;
      },
      error: () => {
        this.approvers = [];
        this.approversFiltered = [];
      },
    });
  }

  private resetApproverFilter(): void {
    this.approvers = [];
    this.approversFiltered = [];
    this.approverInputText = '';
    this.filters.approver = '';
    this.showApproverList = false;
  }

  /** Close all typeahead dropdowns (only one should be open at a time). */
  private closeCombos(): void {
    this.showGlList = false;
    this.showVendorList = false;
    this.showApproverList = false;
  }

  // ---- Approver typeahead combo behavior ----
  approverFocus(): void {
    if (!this.filters.company) return;
    this.closeCombos();
    this.showApproverList = true;
    this.approversFiltered = this.approvers;
  }

  filterApprovers(event: KeyboardEvent): void {
    this.showApproverList = true;
    // #274982 - typed text IS the filter (case-insensitive via the DB's LIKE collation).
    // Selecting a row still overwrites this with the exact value.
    this.filters.approver = this.approverInputText.trim();
    if (event?.key === 'Enter') {
      if (this.approversFiltered.length === 1) this.approverSelected(this.approversFiltered[0]);
      return;
    }
    const t = this.approverInputText.toLowerCase();
    this.approversFiltered = this.approvers.filter((a) => a.value.toLowerCase().indexOf(t) > -1);
  }

  approverSelected(opt: LookupOption): void {
    this.approverInputText = opt.value;
    this.filters.approver = opt.value;
    this.closeApproverDD();
  }

  clearApprover(): void {
    this.approverInputText = '';
    this.filters.approver = '';
    this.approversFiltered = this.approvers;
    this.closeApproverDD();
  }

  closeApproverDD(): void {
    this.showApproverList = false;
  }

  loadVendors(): void {
    this.svc.getVendors(this.filters.company, this.filters.category).subscribe({
      next: (v) => {
        this.vendors = v;
        this.vendorsFiltered = v;
      },
      error: () => {
        this.vendors = [];
        this.vendorsFiltered = [];
      },
    });
  }

  private resetVendorFilter(): void {
    this.vendors = [];
    this.vendorsFiltered = [];
    this.vendorInputText = '';
    this.filters.vendor = '';
    this.showVendorList = false;
  }

  // ---- Vendor typeahead combo behavior (mirrors the GL combo / EDI Select Vendor) ----
  vendorFocus(): void {
    if (!this.filters.company) return;
    this.closeCombos();
    this.showVendorList = true;
    this.vendorsFiltered = this.vendors;
  }

  filterVendors(event: KeyboardEvent): void {
    this.showVendorList = true;
    // #274982 - typed text IS the filter (matches VendorID or VendorName, case-insensitive via the
    // DB's LIKE collation). Selecting a row still overwrites this with the exact VendorID.
    this.filters.vendor = this.vendorInputText.trim();
    if (event?.key === 'Enter') {
      if (this.vendorsFiltered.length === 1) this.vendorSelected(this.vendorsFiltered[0]);
      return;
    }
    const t = this.vendorInputText.toLowerCase();
    this.vendorsFiltered = this.vendors.filter(
      (v) =>
        v.value.toLowerCase().indexOf(t) > -1 ||
        (v.label ?? '').toLowerCase().indexOf(t) > -1,
    );
  }

  vendorSelected(opt: LookupOption): void {
    this.vendorInputText = opt.value;
    this.filters.vendor = opt.value;
    this.closeVendorDD();
  }

  clearVendor(): void {
    this.vendorInputText = '';
    this.filters.vendor = '';
    this.vendorsFiltered = this.vendors;
    this.closeVendorDD();
  }

  closeVendorDD(): void {
    this.showVendorList = false;
  }

  loadGlAccounts(): void {
    this.svc.getGlAccounts(this.filters.company).subscribe({
      next: (g) => {
        this.glAccounts = g;
        this.glFiltered = g;
      },
      error: () => {
        this.glAccounts = [];
        this.glFiltered = [];
      },
    });
  }

  private resetGlFilter(): void {
    this.glAccounts = [];
    this.glFiltered = [];
    this.glInputText = '';
    this.filters.glAcct = '';
    this.showGlList = false;
  }

  // ---- GL typeahead combo behavior (mirrors the accounting EDI "Select Vendor" combo) ----
  glFocus(): void {
    if (!this.filters.company) return;
    this.closeCombos();
    this.showGlList = true;
    this.glFiltered = this.glAccounts;
  }

  filterGl(event: KeyboardEvent): void {
    this.showGlList = true;
    // #274982 - typed text IS the filter (case-insensitive via the DB's LIKE collation).
    // Selecting a row still overwrites this with the exact value.
    this.filters.glAcct = this.glInputText.trim();
    if (event?.key === 'Enter') {
      if (this.glFiltered.length === 1) this.glSelected(this.glFiltered[0]);
      return;
    }
    const t = this.glInputText.toLowerCase();
    this.glFiltered = this.glAccounts.filter(
      (g) =>
        g.value.toLowerCase().indexOf(t) > -1 ||
        (g.label ?? '').toLowerCase().indexOf(t) > -1,
    );
  }

  glSelected(opt: LookupOption): void {
    this.glInputText = opt.value;
    this.filters.glAcct = opt.value;
    this.closeGlDD();
  }

  clearGl(): void {
    this.glInputText = '';
    this.filters.glAcct = '';
    this.glFiltered = this.glAccounts;
    this.closeGlDD();
  }

  closeGlDD(): void {
    this.showGlList = false;
  }

  /** Close any open typeahead dropdown when clicking outside all combos. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if ((this.showGlList || this.showVendorList || this.showApproverList) &&
        !(event.target as HTMLElement).closest('.combo')) {
      this.closeCombos();
    }
    // #274982 - close the classification checkbox dropdown on an outside click.
    if (this.classificationMenuOpen && !(event.target as HTMLElement).closest('.ms-dropdown')) {
      this.classificationMenuOpen = false;
    }
  }

  /** Categories come from the user's Access roles for the selected company (security-gated). */
  private loadCategoriesFromAccess(): void {
    const co = this.access?.companies.find((c) => c.companyCode === this.filters.company);
    const cats = co?.categories ?? [];
    this.categories = cats.map((cat) => ({ value: cat, label: cat }));
    // Don't default the category (AP vs AR) - leave it on "Select…" so the user picks.
    this.filters.category = '';
  }

  loadClassifications(): void {
    this.svc.getClassifications(this.filters.company, this.filters.category).subscribe({
      next: (c) => (this.classifications = c),
      error: () => (this.classifications = []),
    });
  }

  /** True when a category is selected that the viewer doesn't support yet (e.g. AR). */
  get showComingSoon(): boolean {
    return !!this.filters.category && !ApViewerComponent.SupportedCategories.includes(this.filters.category);
  }

  /** True when the CURRENT results are AR (drives grid headers, minimal expand, endpoints). */
  get isAr(): boolean {
    return this.activeCategory === 'AR';
  }

  /** GL Acct filter is AP-only. */
  get showGlAcct(): boolean {
    return this.filters.category === 'AP';
  }

  /** True if the user holds the "Download Manual Checks" access role (149). */
  get canDownloadManualChecks(): boolean {
    return !!this.access?.canDownloadManualChecks;
  }

  // ---- Download Manual Checks ----
  manualChecksLoading = false;
  // The check number we last auto-imported for on a search miss, to avoid re-hammering the
  // full both-company import on repeated searches for the same (genuinely absent) check.
  private manualCheckAutoImportFor: string | null = null;

  /** Badge click: import ALL pending checks for BOTH companies (no prompt), then show a summary. */
  onDownloadManualChecks(): void {
    if (this.manualChecksLoading) return;
    this.runManualCheckImport({
      silent: false,
      onComplete: (imported) => {
        if (imported > 0 && this.canRefresh) this.refresh();
      },
    });
  }

  /**
   * Runs the both-company import (POST process). silent=false shows the combined summary modal
   * (badge button); silent=true suppresses it (search-miss). onComplete(importedCount) fires on
   * success only.
   */
  private runManualCheckImport(opts: { silent: boolean; onComplete?: (imported: number) => void }): void {
    this.manualChecksLoading = true;
    this.svc.processManualChecks().subscribe({
      next: (r) => {
        this.manualChecksLoading = false;
        const results = r.results ?? [];
        const imported = results.filter((x) => x.status === 'imported').length;
        const skipped = results.filter((x) => x.status === 'skipped-existing').length;
        const notInGp = results.filter((x) => x.status === 'gp-not-found').length;
        const errors = results.filter((x) => x.status === 'error');

        if (!opts.silent) {
          let msg = `Imported ${imported}, skipped ${skipped} (already present)`;
          if (notInGp) msg += `, ${notInGp} not found in GP`;
          if (errors.length) msg += `, ${errors.length} error(s)`;
          msg += '.';
          if (errors.length) msg += `\n\nFirst error (${errors[0].fileName}): ${errors[0].error}`;
          this.mcAlert(msg);
        }
        console.log('Manual checks import', r);
        opts.onComplete?.(imported);
      },
      error: (e) => {
        this.manualChecksLoading = false;
        console.error('Manual checks import failed', e);
        this.mcAlert(e?.error?.error || e?.message || 'Manual checks import failed.');
      },
    });
  }

  // ---- Reusable manual-checks message/confirm modal (replaces window.alert/confirm) ----
  mcModalOpen = false;
  mcModalMessage = '';
  mcModalConfirm = false;            // true => show Cancel (confirm); false => OK-only (alert)
  private mcModalOnOk: (() => void) | null = null;

  private mcAlert(message: string): void {
    this.mcModalMessage = message;
    this.mcModalConfirm = false;
    this.mcModalOnOk = null;
    this.mcModalOpen = true;
  }

  mcModalOk(): void {
    const cb = this.mcModalOnOk;
    this.mcModalOpen = false;
    this.mcModalOnOk = null;
    if (cb) cb();
  }

  mcModalCancel(): void {
    this.mcModalOpen = false;
    this.mcModalOnOk = null;
  }

  // ---- Category-aware filter labels ----
  // The same party field is a "Vendor" for AP but a "Customer" for AR.
  get partyLabel(): string {
    return this.filters.category === 'AR' ? 'CUSTOMER' : 'VENDOR';
  }
  get partyPlaceholder(): string {
    return this.filters.category === 'AR' ? 'Search customer #/name…' : 'Search vendor #/name…';
  }

  // ---- Category-specific filter visibility ----
  /** Approver applies to AP documents only. */
  get showApprover(): boolean {
    return this.filters.category === 'AP';
  }

  /** Required filters mirror the API guard: Category, Classification, Date From, Date To. */
  /** A Manual Check search keyed on a check number - ignores the date range (see canRefresh). */
  get isCheckLookup(): boolean {
    return this.isManualCheckClassification && !!this.filters.checkNumber?.trim();
  }

  get canRefresh(): boolean {
    if (!this.filters.company || !this.filters.category || this.selectedClassificationIds.length === 0) return false;
    if (this.isCheckLookup) return true;   // check # + company only; dates not required
    return !!this.filters.dateFrom && !!this.filters.dateTo;
  }

  get missingFilters(): string[] {
    const missing: string[] = [];
    if (!this.filters.company) missing.push('Company');
    if (this.selectedClassificationIds.length === 0) missing.push('Classification');
    if (!this.isCheckLookup) {              // dates aren't required for a check # lookup
      if (!this.filters.dateFrom) missing.push('Date From');
      if (!this.filters.dateTo) missing.push('Date To');
    }
    return missing;
  }

  refresh(page = 1, isAutoRetry = false): void {
    if (this.showComingSoon) return; // unsupported category: never run an AP query
    if (!this.canRefresh) return;
    this.filters.page = page;
    this.loading = true;
    this.error = null;
    this.expandedId = null;
    this.closePane();   // a new search has no selected row -> clear the right-hand document pane
    this.svc.search(this.filters).subscribe({
      next: (r) => {
        this.result = r;
        this.activeCategory = this.filters.category || 'AP'; // results now belong to this category
        this.hasSearched = true;
        this.loading = false;
        this.maybeAutoImportCheck(r.totalCount ?? 0, isAutoRetry);
      },
      error: (e) => {
        this.error = e?.error?.error || e?.message || 'Search failed.';
        this.result = null;
        this.loading = false;
      },
    });
  }

  /**
   * Check-# search that returned nothing: pull ALL pending checks from BOTH companies (the same
   * import the badge runs, but silent and not 149-gated), then re-search. If it's still missing
   * after that, it isn't in DocRepo or SharePoint. Guarded so it runs at most once per check.
   */
  private maybeAutoImportCheck(totalCount: number, isAutoRetry: boolean): void {
    if (!this.isCheckLookup || totalCount > 0) return;
    const check = this.filters.checkNumber!.trim();

    if (isAutoRetry || this.manualCheckAutoImportFor === check) {
      // Already imported + re-searched (or already tried for this exact check) and still nothing.
      this.mcAlert(`Check ${check} was not found in DocRepo or SharePoint.`);
      return;
    }
    this.manualCheckAutoImportFor = check;
    this.runManualCheckImport({ silent: true, onComplete: () => this.refresh(this.filters.page, true) });
  }

  /** Click a sortable column header: toggle direction if same column, else sort that column ascending. */
  sort(column: string): void {
    if (this.filters.sortBy === column) {
      this.filters.sortDir = this.filters.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.filters.sortBy = column;
      this.filters.sortDir = 'asc';
    }
    this.refresh(1);
  }

  sortIndicator(column: string): string {
    if (this.filters.sortBy !== column) return '';
    return this.filters.sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  toggleRow(doc: DocumentSummary): void {
    if (this.expandedId === doc.azureDocId) {
      this.expandedId = null;
      return;
    }
    this.expandedId = doc.azureDocId;

    const cached = this.details[doc.azureDocId];
    if (cached) {
      this.showPane(cached, 0); // auto-open the first document
      return;
    }
    this.detailLoading = true;
    this.svc.getDetail(doc.azureDocId, this.activeCategory).subscribe({
      next: (d) => {
        this.details[doc.azureDocId] = d;
        this.detailLoading = false;
        this.showPane(d, 0); // auto-open the first document
      },
      error: () => (this.detailLoading = false),
    });
  }

  openPane(doc: DocumentSummary, index = 0): void {
    const cached = this.details[doc.azureDocId];
    if (cached) {
      this.showPane(cached, index);
      return;
    }
    this.svc.getDetail(doc.azureDocId, this.activeCategory).subscribe({
      next: (d) => {
        this.details[doc.azureDocId] = d;
        this.showPane(d, index);
      },
    });
  }

  private showPane(d: DocumentDetail, index: number): void {
    this.paneDoc = d;
    this.paneIndex = index;
    this.paneOpen = true;
    this.resetEmailForm();
    this.setupPreview(d, index);
  }

  private resetEmailForm(): void {
    this.emailFormOpen = false;
    this.emailTo = '';
    this.emailMessage = '';
    this.emailSending = false;
    this.emailError = null;
    this.emailSent = false;
  }

  /** File names in the same order the API's file endpoint uses (index-aligned):
   *  multi-file docs list AzureDocsFiles; single-file docs use the primary FileName. */
  private fileNames(d: DocumentDetail): string[] {
    if (d.files && d.files.length) return d.files.map((f) => f.fileName);
    return d.fileName ? [d.fileName] : [];
  }

  /** Choose how to render the file: PDF/text inline (iframe), images inline (img),
   *  TIFF/other → download-only (browsers can't render them). */
  private setupPreview(d: DocumentDetail, index: number): void {
    const names = this.fileNames(d);
    const name = names[index] ?? names[0] ?? null;
    this.paneFileName = name;
    this.paneSafeUrl = null;
    this.paneRawUrl = null;
    this.paneForm = null;

    if (!name) {
      this.paneFileKind = 'none';
      return;
    }
    const ext = (name.split('.').pop() || '').toLowerCase();
    const url = this.svc.fileUrl(d.azureDocId, index, this.activeCategory);
    this.paneRawUrl = url;

    if (ext === 'pdf') {
      this.paneFileKind = 'pdf';
      this.paneSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) {
      this.paneFileKind = 'image';
    } else if (['tif', 'tiff'].includes(ext)) {
      this.paneFileKind = 'tiff';
    } else if (ext === 'xml') {
      // Try to render OnBase Unity e-forms (GL Coding Form etc.) as a clean form;
      // fall back to the raw iframe view if it isn't a recognizable form.
      this.paneFileKind = 'text';
      this.paneSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      const forId = d.azureDocId;
      const forIndex = index;
      this.svc.getFileText(d.azureDocId, index, this.activeCategory).subscribe({
        next: (xml) => {
          // Ignore if the user has since switched files.
          if (this.paneDoc?.azureDocId !== forId || this.paneIndex !== forIndex) return;
          const form = this.parseOnBaseForm(xml);
          if (form && (form.fields.length || form.tables.length)) {
            this.paneForm = form;
            this.paneFileKind = 'xmlform';
          }
        },
        error: () => {
          /* keep the iframe fallback already set */
        },
      });
    } else if (['html', 'txt'].includes(ext)) {
      this.paneFileKind = 'text';
      this.paneSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.paneFileKind = 'other';
    }
  }

  /**
   * Parses an OnBase Unity e-form (root &lt;form xmlns="http://hyland.com/schemas/formdata"&gt;)
   * into header fields + repeater tables. Matches by element local name to stay namespace-robust,
   * mirroring the migrator's GlCodingFormParser. Returns null if the XML isn't a form.
   */
  private parseOnBaseForm(xml: string): ParsedForm | null {
    let doc: Document;
    try {
      doc = new DOMParser().parseFromString(xml, 'application/xml');
    } catch {
      return null;
    }
    if (doc.getElementsByTagName('parsererror').length) return null;

    const byName = (root: Element | Document, ln: string) =>
      Array.from(root.getElementsByTagNameNS('*', ln));
    const childVal = (el: Element, ln: string): string => {
      const c = Array.from(el.children).find((x) => x.localName === ln);
      return c ? (c.textContent || '').trim() : '';
    };
    const insideRepeater = (el: Element): boolean => {
      let p = el.parentElement;
      while (p) {
        if (p.localName === 'repeater') return true;
        p = p.parentElement;
      }
      return false;
    };

    const allFields = byName(doc, 'field');
    if (!allFields.length) return null;

    // Header fields = <field> not inside a repeater.
    const fields = allFields
      .filter((f) => !insideRepeater(f))
      .map((f) => ({ name: childVal(f, 'name'), value: childVal(f, 'value') }))
      .filter((f) => f.name && f.value)
      .map((f) => ({ label: this.formLabel(f.name), value: this.formValue(f.name, f.value) }));

    // Repeater tables (e.g. the GL distribution).
    const tables = byName(doc, 'repeater')
      .map((r) => {
        const columns: string[] = [];
        const rows: Record<string, string>[] = [];
        Array.from(r.children)
          .filter((c) => c.localName === 'repeateritem')
          .forEach((item) => {
            const row: Record<string, string> = {};
            Array.from(item.children)
              .filter((c) => c.localName === 'field')
              .forEach((fl) => {
                const rawName = childVal(fl, 'name');
                if (!rawName) return;
                const label = this.formLabel(rawName);
                if (!columns.includes(label)) columns.push(label);
                row[label] = this.formValue(rawName, childVal(fl, 'value'));
              });
            if (Object.values(row).some((v) => v && v.trim())) rows.push(row);
          });
        return { title: this.formLabel(childVal(r, 'name')), columns, rows };
      })
      .filter((t) => t.rows.length);

    return { fields, tables };
  }

  /** Friendly label for a cryptic OnBase field name (e.g. "invoicedate7" -> "Invoice Date"). */
  private formLabel(rawName: string): string {
    const key = rawName.replace(/\d+$/, '').toLowerCase();
    const map: Record<string, string> = {
      invoice: 'Invoice #',
      invoicedate: 'Invoice Date',
      invoiceamount: 'Invoice Amount',
      vendorname: 'Vendor Name',
      vendorid: 'Vendor ID',
      vendoridhcs: 'Vendor ID',
      vendoridllc: 'Vendor ID',
      companyid: 'Company ID',
      amountremaining: 'Amount Remaining',
      glcodinghcs: 'GL Distribution',
      glcoding: 'GL Distribution',
      glcode: 'GL Code',
      glcodehcs: 'GL Code',
      gldescription: 'Description',
      glamount: 'Amount',
      glpercentage: '%',
    };
    if (map[key]) return map[key];
    // Fallback: strip trailing digits, split camelCase / underscores, title-case.
    let s = rawName.replace(/\d+$/, '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    return s.replace(/\b\w/g, (c) => c.toUpperCase()).trim() || rawName;
  }

  /** Formats a field value for display (currency for amount fields, blank -> em dash). */
  private formValue(rawName: string, value: string): string {
    const v = (value || '').trim();
    if (!v) return '—';
    const key = rawName.replace(/\d+$/, '').toLowerCase();
    const isAmount = key.includes('amount') || key === 'glamount';
    if (isAmount) {
      const n = Number(v.replace(/[$,]/g, ''));
      if (!isNaN(n)) return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return v;
  }

  closePane(): void {
    this.paneOpen = false;
    this.paneDoc = null;
    this.paneIndex = 0;
    this.paneSafeUrl = null;
    this.paneRawUrl = null;
    this.paneFileName = null;
    this.paneFileKind = 'none';
    this.paneForm = null;
    this.resetEmailForm();
  }

  /** Grid display: drop the redundant "LLC - " / "HCS - " company prefix from a classification. */
  classificationLabel(value: string | null | undefined): string {
    return (value ?? '').replace(/^(LLC|HCS)\s*-\s*/i, '');
  }

  download(): void {
    if (this.paneDoc) {
      window.open(this.svc.fileDownloadUrl(this.paneDoc.azureDocId, this.paneIndex, this.activeCategory), '_blank');
    }
  }

  /** Open the current file inline in its own browser tab for full-window viewing.
   *  For parsed XML e-forms, open the SAME formatted layout as the pane (not raw XML). */
  openInTab(): void {
    if (!this.paneDoc) return;
    if (this.paneFileKind === 'xmlform' && this.paneForm) {
      this.openFormInTab(this.paneForm);
      return;
    }
    window.open(this.svc.fileUrl(this.paneDoc.azureDocId, this.paneIndex, this.activeCategory), '_blank');
  }

  /** Render a parsed e-form as a standalone, styled HTML page in a new tab. */
  private openFormInTab(form: ParsedForm): void {
    const esc = (s: string): string =>
      (s ?? '').replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string,
      );
    const heading = [this.paneDoc?.vendorName, this.paneDoc?.invoiceNumber]
      .filter(Boolean)
      .join(' — ');

    const fieldsHtml = form.fields
      .map((f) => `<div class="row"><dt>${esc(f.label)}</dt><dd>${esc(f.value)}</dd></div>`)
      .join('');
    const tablesHtml = form.tables
      .map((t) => {
        const head = t.columns.map((c) => `<th>${esc(c)}</th>`).join('');
        const body = t.rows
          .map((r) => `<tr>${t.columns.map((c) => `<td>${esc(r[c] || '—')}</td>`).join('')}</tr>`)
          .join('');
        return `<h2>${esc(t.title)}</h2><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
      })
      .join('');

    const html = `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(heading || 'Document')}</title>
<style>
  :root { --navy:#123b4a; --blue:#006792; --line:#e2e0d8; --muted:#6b7378; --cream:#f3f1ec; }
  * { box-sizing: border-box; }
  body { margin:0; font-family:"Myriad Pro", Calibri, sans-serif; color:#231F20; background:#fff; }
  header { background:var(--navy); color:#fff; padding:12px 20px; font-weight:700; letter-spacing:.3px;
           font-family:"DIN 2014", Bahnschrift, Arial, sans-serif; }
  header small { display:block; font-weight:400; font-size:12px; opacity:.75; letter-spacing:.5px; }
  main { max-width:900px; margin:0 auto; padding:22px 24px; }
  dl.fields { display:grid; grid-template-columns:max-content 1fr; gap:6px 20px; margin:0 0 22px; }
  dl.fields .row { display:contents; }
  dl.fields dt { font-size:11px; font-weight:700; letter-spacing:.4px; text-transform:uppercase; color:var(--muted); align-self:center; }
  dl.fields dd { margin:0; font-size:14px; }
  h2 { margin:20px 0 8px; font-size:12px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; color:var(--blue);
       font-family:"DIN 2014", Bahnschrift, Arial, sans-serif; }
  table { width:100%; border-collapse:collapse; border:1px solid var(--line); border-radius:6px; overflow:hidden; }
  th { background:#f2f4f5; color:var(--navy); text-align:left; font-size:11px; font-weight:700; letter-spacing:.4px;
       text-transform:uppercase; padding:8px 12px; border-bottom:1px solid var(--line); }
  td { padding:8px 12px; border-bottom:1px solid var(--line); font-size:13px; }
  tr:last-child td { border-bottom:none; }
</style></head><body>
<header>${esc(heading || 'Document')}<small>HARRELL'S DOC REPO</small></header>
<main>${fieldsHtml ? `<dl class="fields">${fieldsHtml}</dl>` : ''}${tablesHtml}</main>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    }
  }

  /** Friendly file-type label from the extension (e.g. "PDF", "TIFF image"). */
  fileTypeLabel(name: string | null): string {
    const ext = (name?.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'PDF';
      case 'tif':
      case 'tiff': return 'TIFF image';
      case 'jpg':
      case 'jpeg': return 'JPEG image';
      case 'png': return 'PNG image';
      case 'gif': return 'GIF image';
      case 'bmp': return 'BMP image';
      case 'xml': return 'XML e-form';
      case 'html': return 'HTML';
      case 'txt': return 'Text';
      case 'msg': return 'Email';
      case 'doc':
      case 'docx': return 'Word';
      case 'xls':
      case 'xlsx': return 'Excel';
      default: return ext ? ext.toUpperCase() : 'File';
    }
  }

  /** Short source label for a group file (strips entity prefix: "LLC - Expense Invoice" -> "Expense Invoice"). */
  fileSourceLabel(f: DocFile): string {
    const c = f.sourceClassification || '';
    const dash = c.indexOf(' - ');
    return dash >= 0 ? c.substring(dash + 3) : c;
  }

  /** Material icon for a file based on its extension. */
  fileIcon(name: string | null): string {
    const ext = (name?.split('.').pop() || '').toLowerCase();
    if (ext === 'pdf') return 'picture_as_pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff'].includes(ext)) return 'image';
    return 'insert_drive_file';
  }

  /** True when the pane is showing this doc's file at the given index (to highlight the active button). */
  isActiveFile(azureDocId: number, index: number): boolean {
    return this.paneOpen && this.paneDoc?.azureDocId === azureDocId && this.paneIndex === index;
  }

  /** Toggle the inline email form open/closed, resetting its transient state. */
  email(): void {
    if (!this.paneDoc) return;
    this.emailFormOpen = !this.emailFormOpen;
    this.emailError = null;
    this.emailSent = false;
  }

  cancelEmail(): void {
    this.emailFormOpen = false;
    this.emailError = null;
  }

  /** Send the currently-viewed file to the entered recipient as a real attachment. */
  sendEmail(): void {
    if (!this.paneDoc || this.emailSending) return;

    const to = this.emailTo.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      this.emailError = 'Please enter a valid email address.';
      return;
    }

    this.emailSending = true;
    this.emailError = null;
    this.emailSent = false;

    this.svc
      .emailDocument(this.paneDoc.azureDocId, to, this.paneIndex, this.emailMessage.trim(), this.activeCategory)
      .subscribe({
        next: () => {
          this.emailSending = false;
          this.emailSent = true;
          this.emailFormOpen = false;
          this.emailTo = '';
          this.emailMessage = '';
        },
        error: (err) => {
          this.emailSending = false;
          this.emailError =
            err?.error?.error || 'The document could not be emailed. Please try again.';
        },
      });
  }

  // ---- Paging helpers ----
  get pageStart(): number {
    if (!this.result || this.result.totalCount === 0) return 0;
    return (this.result.page - 1) * this.result.pageSize + 1;
  }
  get pageEnd(): number {
    if (!this.result) return 0;
    return Math.min(this.result.page * this.result.pageSize, this.result.totalCount);
  }
  prevPage(): void {
    if (this.result && this.result.page > 1) this.refresh(this.result.page - 1);
  }
  nextPage(): void {
    if (this.result && this.result.page < this.result.totalPages) {
      this.refresh(this.result.page + 1);
    }
  }

  trackById(_: number, doc: DocumentSummary): number {
    return doc.azureDocId;
  }
}
